import React, { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Routes as RouterRoutes, Route, useLocation, useNavigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import AppLoader from "components/ui/AppLoader";
import FloatingChatbot from "components/FloatingChatbot";
import { apiGetBlockedPages, apiValidateSession, hasCachedBlockedPagesSnapshot, readCachedBlockedPages } from "utils/authApi";
import { isNetworkUnavailable } from "utils/offlineErp";
import { hasEffectiveActiveSubscription, hasOrganizationScope, resolvePostLoginPath, resolveRoleHomePath, useErpContext } from "utils/erpContext";

const LandingSplash = lazy(() => import("./pages/landing-splash"));
const LandingPage = lazy(() => import("./pages/landing"));
const PublicSplashPage = lazy(() => import("./pages/splash"));
const SplashHomePage = lazy(() => import("./pages/splash-home"));
const LoginPage = lazy(() => import("./pages/login"));
const ChangePasswordPage = lazy(() => import("./pages/alterar-senha"));
const RegisterPage = lazy(() => import("./pages/register"));
const CompanySetupPage = lazy(() => import("./pages/company-setup"));
const ManagerOverview = lazy(() => import("./pages/manager-overview"));
const CeoDashboard = lazy(() => import("./pages/ceo-dashboard"));
const StoreManagerDashboard = lazy(() => import("./pages/store-manager-dashboard"));
const ExecutiveKpiDashboard = lazy(() => import("./pages/executive-kpi-dashboard"));
const FinancialComplianceHub = lazy(() => import("./pages/financial-compliance-hub"));
const AgtManagement = lazy(() => import("./pages/agt-management"));
const SalesAnalyticsDashboard = lazy(() => import("./pages/sales-analytics-dashboard"));
const PosTerminal = lazy(() => import("./pages/pos-terminal"));
const PosSaleSuccess = lazy(() => import("./pages/pos-sale-success"));
const CustomerRegistry = lazy(() => import("./pages/customer-registry"));
const ProductCatalog = lazy(() => import("./pages/product-catalog"));
const Transacoes = lazy(() => import("./pages/transacoes"));
const TransacoesAjustes = lazy(() => import("./pages/transacoes-ajustes"));
const TransacoesValidar = lazy(() => import("./pages/transacoes-validar"));
const TransacoesDocumentos = lazy(() => import("./pages/transacoes-documentos"));
const TransacoesDocumentosLista = lazy(() => import("./pages/transacoes-documentos-lista"));
const ProdutosVendidosPage = lazy(() => import("./pages/produtos-vendidos"));
const ObservabilityEventsPage = lazy(() => import("./pages/observability-events"));
const AssistantTelemetryPage = lazy(() => import("./pages/assistant-telemetry"));
const CobrancasPage = lazy(() => import("./pages/cobrancas"));
const PosDailyReportPage = lazy(() => import("./pages/pos-daily-report"));
const SubscriptionPlansPage = lazy(() => import("./pages/subscription-plans"));
const AdminSignatureLicensesPage = lazy(() => import("./pages/admin-signature-licenses"));
const AdminPanelPage = lazy(() => import("./pages/admin-painel"));
const AccessControlPage = lazy(() => import("./pages/access-control"));
const SettingsPage = lazy(() => import("./pages/settings"));
const ProfileSettingsPage = lazy(() => import("./pages/profile-settings"));
const VendorManagementPage = lazy(() => import("./pages/vendor-management"));
const NotFound = lazy(() => import("pages/NotFound"));
const MaintenancePage = lazy(() => import("pages/maintenance"));

const INDEXABLE_PUBLIC_PATHS = new Set(["/landing"]);

function RouteSeoGuard() {
  const location = useLocation();

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const path = location?.pathname || "/";
    const shouldIndex = INDEXABLE_PUBLIC_PATHS.has(path);
    const robotsContent = shouldIndex
      ? "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"
      : "noindex,nofollow,max-image-preview:none,max-snippet:0,max-video-preview:0";

    ["robots", "googlebot", "bingbot"].forEach((name) => {
      let tag = document.head.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", robotsContent);
    });
  }, [location?.pathname]);

  return null;
}

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--color-background)" }}>
      <AppLoader title="Aguarde..." message="A preparar o próximo módulo com segurança." compact />
    </div>
  );
}

// ── Maintenance pages cache ─────────────────────────────────────────────────
// Papéis que podem ver páginas em manutenção sem serem bloqueados
const MAINTENANCE_BYPASS_ROLES = ["executive", "admin"];

// Cache em módulo para não refetch a cada remount
let _blockedPagesCache = readCachedBlockedPages();
let _blockedPagesCacheAt = _blockedPagesCache?.length ? Date.now() : 0;
let _blockedPagesTrusted = hasCachedBlockedPagesSnapshot();
const BLOCKED_PAGES_TTL_MS = 2 * 60 * 1000; // 2 min

async function fetchBlockedPages() {
  const now = Date.now();
  if (_blockedPagesCache !== null && now - _blockedPagesCacheAt < BLOCKED_PAGES_TTL_MS) {
    return {
      pages: _blockedPagesCache,
      trusted: _blockedPagesTrusted,
    };
  }

  const result = await apiGetBlockedPages({ includeMeta: true });
  const pages = Array.isArray(result?.items) ? result.items : [];

  _blockedPagesCache = pages;
  _blockedPagesCacheAt = now;
  _blockedPagesTrusted = Boolean(result?.trusted);

  return {
    pages,
    trusted: _blockedPagesTrusted,
  };
}

function useBlockedPages() {
  const [blockedPagesState, setBlockedPagesState] = React.useState(() => ({
    pages: Array.isArray(_blockedPagesCache) ? _blockedPagesCache : readCachedBlockedPages(),
    trusted: _blockedPagesTrusted,
    ready: false,
  }));

  React.useEffect(() => {
    let active = true;
    fetchBlockedPages().then((result) => {
      if (!active) return;

      setBlockedPagesState({
        pages: Array.isArray(result?.pages) ? result.pages : [],
        trusted: Boolean(result?.trusted),
        ready: true,
      });
    });
    return () => { active = false; };
  }, []);

  return blockedPagesState;
}

const SPLASH_EXIT_MS = 680;
const SPLASH_INACTIVITY_MS = 30 * 60 * 1000;
const AUTH_HEARTBEAT_MS = 60 * 1000;
const ERP_SESSION_EXPIRED_EVENT = "angolabi:erp-session-expired";

function isOfflineSessionToken(token) {
  return String(token || "").trim() === "offline-session";
}
const SPLASH_STORAGE_KEYS = {
  lastActivityAt: "angolabi.splash.lastActivityAt",
  lastDismissedAt: "angolabi.splash.lastDismissedAt",
  forceShow: "angolabi.splash.forceShow"
};

function readSplashTimestamp(key) {
  if (typeof window === "undefined") return 0;

  const rawValue = Number(window.localStorage.getItem(key));
  return Number.isFinite(rawValue) ? rawValue : 0;
}

function writeSplashTimestamp(key, value) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(key, String(value));
}

function shouldShowStartupSplash() {
  if (typeof window === "undefined") return true;

  const now = Date.now();
  const forceShow = window.localStorage.getItem(SPLASH_STORAGE_KEYS.forceShow) === "1";
  const lastDismissedAt = readSplashTimestamp(SPLASH_STORAGE_KEYS.lastDismissedAt);
  const lastActivityAt = readSplashTimestamp(SPLASH_STORAGE_KEYS.lastActivityAt);

  if (forceShow) return true;
  if (!lastDismissedAt) return true;

  const referenceTimestamp = Math.max(lastDismissedAt, lastActivityAt);
  return now - referenceTimestamp >= SPLASH_INACTIVITY_MS;
}

function StartupSplashGate() {
  const location = useLocation();
  const { isAuthenticated } = useErpContext();
  const [visible, setVisible] = React.useState(shouldShowStartupSplash);
  const [exiting, setExiting] = React.useState(false);
  const initialPathRef = React.useRef(location?.pathname);
  const exitTimerRef = React.useRef(null);
  const inactivityTimerRef = React.useRef(null);
  const lastActivityWriteRef = React.useRef(0);
  const currentPath = String(location?.pathname || "");
  const isHomeSplashPath = currentPath === "/home-splash";

  const clearInactivityTimer = React.useCallback(() => {
    if (inactivityTimerRef.current) {
      window.clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  const armInactivityTimer = React.useCallback(() => {
    if (typeof window === "undefined") return;

    clearInactivityTimer();
    inactivityTimerRef.current = window.setTimeout(() => {
      setExiting(false);
      setVisible(true);
    }, SPLASH_INACTIVITY_MS);
  }, [clearInactivityTimer]);

  const registerActivity = React.useCallback(() => {
    const now = Date.now();

    if (now - lastActivityWriteRef.current >= 15000) {
      writeSplashTimestamp(SPLASH_STORAGE_KEYS.lastActivityAt, now);
      lastActivityWriteRef.current = now;
    }
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    if (!isAuthenticated || location?.pathname === "/login" || isHomeSplashPath) {
      setExiting(false);
      setVisible(false);
      return;
    }

    if (shouldShowStartupSplash()) {
      setExiting(false);
      setVisible(true);
    }
  }, [isAuthenticated, isHomeSplashPath, location?.pathname]);

  const beginDismiss = React.useCallback(() => {
    setExiting((current) => {
      if (current) return current;

      const now = Date.now();
      writeSplashTimestamp(SPLASH_STORAGE_KEYS.lastDismissedAt, now);
      writeSplashTimestamp(SPLASH_STORAGE_KEYS.lastActivityAt, now);
      window.localStorage.removeItem(SPLASH_STORAGE_KEYS.forceShow);
      lastActivityWriteRef.current = now;
      clearInactivityTimer();

      exitTimerRef.current = window.setTimeout(() => {
        setVisible(false);
      }, SPLASH_EXIT_MS);

      return true;
    });
  }, [clearInactivityTimer]);

  React.useEffect(() => {
    if (!isAuthenticated || location?.pathname === "/login" || isHomeSplashPath) return;

    if (!visible && shouldShowStartupSplash()) {
      setExiting(false);
      setVisible(true);
      return;
    }

    if (visible) {
      initialPathRef.current = location?.pathname;
    }
  }, [isHomeSplashPath, isAuthenticated, location?.pathname, visible]);

  React.useEffect(() => {
    if (!isAuthenticated || location?.pathname === "/login" || isHomeSplashPath) return;

    if (location?.pathname !== initialPathRef.current) {
      beginDismiss();
    }
  }, [beginDismiss, isAuthenticated, isHomeSplashPath, location?.pathname]);

  React.useEffect(() => {
    if (!visible || typeof window === "undefined") return undefined;

    const dismissSplash = () => beginDismiss();

    window.addEventListener("pointerdown", dismissSplash, true);
    window.addEventListener("keydown", dismissSplash, true);

    return () => {
      window.removeEventListener("pointerdown", dismissSplash, true);
      window.removeEventListener("keydown", dismissSplash, true);
    };
  }, [beginDismiss, visible]);

  React.useEffect(() => {
    if (visible || !isAuthenticated || isHomeSplashPath || typeof window === "undefined") return undefined;

    const activityEvents = ["pointerdown", "keydown", "scroll", "touchstart", "focus"];
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivityWriteRef.current >= 15000) {
        writeSplashTimestamp(SPLASH_STORAGE_KEYS.lastActivityAt, now);
        lastActivityWriteRef.current = now;
      }
    };

    activityEvents.forEach((eventName) => window.addEventListener(eventName, handleActivity, true));
    armInactivityTimer();

    return () => {
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, handleActivity, true));
      clearInactivityTimer();
    };
  }, [armInactivityTimer, clearInactivityTimer, isAuthenticated, isHomeSplashPath, visible]);

  React.useEffect(() => () => {
    if (exitTimerRef.current) {
      window.clearTimeout(exitTimerRef.current);
    }

    clearInactivityTimer();
  }, [clearInactivityTimer]);

  if (!isAuthenticated || location?.pathname === "/login" || isHomeSplashPath || !visible) return null;

  return <LandingSplash isExiting={exiting} />;
}

function AuthSessionGuard() {
  const location = useLocation();
  const { isAuthenticated, authToken, currentUser, refreshAuthSession } = useErpContext();
  const refreshAuthSessionRef = React.useRef(refreshAuthSession);
  const lastHeartbeatRef = React.useRef(0);
  const heartbeatInFlightRef = React.useRef(false);
  const [autoLogoutTransition, setAutoLogoutTransition] = React.useState(false);
  const autoLogoutTimerRef = React.useRef(null);

  React.useEffect(() => {
    refreshAuthSessionRef.current = refreshAuthSession;
  }, [refreshAuthSession]);

  const startAutoLogoutTransition = React.useCallback(() => {
    if (autoLogoutTimerRef.current) {
      window.clearTimeout(autoLogoutTimerRef.current);
      autoLogoutTimerRef.current = null;
    }

    setAutoLogoutTransition(true);
    autoLogoutTimerRef.current = window.setTimeout(() => {
      setAutoLogoutTransition(false);
      autoLogoutTimerRef.current = null;
    }, 450);
  }, []);

  React.useEffect(() => () => {
    if (autoLogoutTimerRef.current) {
      window.clearTimeout(autoLogoutTimerRef.current);
      autoLogoutTimerRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    if (!isAuthenticated || location?.pathname === "/login" || typeof window === "undefined") return undefined;

    const touchServerSession = async (force = false) => {
      if (authToken && isOfflineSessionToken(authToken)) {
        return;
      }

      const now = Date.now();

      if (!force && now - lastHeartbeatRef.current < AUTH_HEARTBEAT_MS) {
        return;
      }

      if (heartbeatInFlightRef.current) return;

      heartbeatInFlightRef.current = true;

      try {
        const refreshed = await refreshAuthSessionRef.current();
        if (!refreshed?.ok) {
          throw new Error(refreshed?.message || "Nao foi possivel sincronizar a sessao.");
        }
        lastHeartbeatRef.current = Date.now();
      } catch (error) {
        // Se a validação falhar, o interceptor de auth trata o 401/expiração.
        // Em falhas transitórias de rede, tentamos de novo no próximo heartbeat.
        if (!isNetworkUnavailable(error)) {
          console.debug("[AUTH] refreshAuthSession falhou; será re-tentado no próximo heartbeat.");
        }
      } finally {
        heartbeatInFlightRef.current = false;
      }
    };

    void touchServerSession(true);

    const handleVisibilityOrFocus = () => {
      void touchServerSession(true);
    };

    window.addEventListener("focus", handleVisibilityOrFocus, true);
    document.addEventListener("visibilitychange", handleVisibilityOrFocus, true);

    const heartbeatInterval = window.setInterval(() => {
      void touchServerSession();
    }, AUTH_HEARTBEAT_MS);

    return () => {
      window.clearInterval(heartbeatInterval);
      window.removeEventListener("focus", handleVisibilityOrFocus, true);
      document.removeEventListener("visibilitychange", handleVisibilityOrFocus, true);
    };
  }, [authToken, isAuthenticated, location?.pathname]);

  React.useEffect(() => {
    if (!isAuthenticated || typeof window === "undefined") {
      return undefined;
    }

    const handleSessionExpiredUiTransition = () => {
      startAutoLogoutTransition();
    };

    window.addEventListener(ERP_SESSION_EXPIRED_EVENT, handleSessionExpiredUiTransition);

    return () => {
      window.removeEventListener(ERP_SESSION_EXPIRED_EVENT, handleSessionExpiredUiTransition);
    };
  }, [isAuthenticated, startAutoLogoutTransition]);

  return autoLogoutTransition ? (
    <div
      className="fixed inset-0 pointer-events-none"
      style={{
        zIndex: 1800,
        background: 'radial-gradient(circle at 50% 30%, rgba(255,255,255,0.35), rgba(255,255,255,0.10) 42%, rgba(11,18,32,0.30) 100%)',
        backdropFilter: 'blur(2px) saturate(1.03)',
        WebkitBackdropFilter: 'blur(2px) saturate(1.03)',
      }}
      aria-hidden="true"
    />
  ) : null;
}

function ProtectedRoute({ children }) {
  const location = useLocation();
  const { isAuthenticated, currentUser, activeCompany, activeStore, logout } = useErpContext();
  const blockedPagesState = useBlockedPages();
  const blockedPages = blockedPagesState?.pages || [];
  const blockedPagesTrusted = Boolean(blockedPagesState?.trusted);
  const blockedPagesReady = Boolean(blockedPagesState?.ready);
  const isOffline = typeof navigator !== "undefined" && navigator.onLine === false;
  const isTauriDesktop = Boolean(
    typeof window !== "undefined"
    && (String(window.location?.hostname || "").toLowerCase() === "tauri.localhost"
      || typeof window.__TAURI_INTERNALS__ !== "undefined")
  );
  const normalizedRole = String(currentUser?.roleValue || "").trim().toLowerCase();
  const isAdminSuper = isAdminSuperUser(currentUser);
  const hasCompanyScope = Boolean(String(currentUser?.companyUuid || "").trim());
  const hasStoreScope = Boolean(String(currentUser?.storeUuid || "").trim());
  const isGeneralManagerRole = normalizedRole === "gerente_geral";
  const isGeneralManagerRoleOnly = normalizedRole === "gerente_geral";
  const baseShouldForceLogoutForMissingStore = Boolean(currentUser && !hasStoreScope && !isGeneralManagerRole && !isAdminSuper);
  const isCashierRole = normalizedRole === "caixa";
  const scopedStoresCount = Array.isArray(activeCompany?.stores)
    ? activeCompany.stores.filter((store) => String(store?.id || "").trim() !== "all").length
    : 0;
  const directCompanyStoreCount = Number(currentUser?.companyStoreCount || 0);
  const companyStoreCount = Number.isFinite(scopedStoresCount) && scopedStoresCount > 0
    ? scopedStoresCount
    : (Number.isFinite(directCompanyStoreCount) && directCompanyStoreCount > 0
      ? directCompanyStoreCount
      : 0);
  const shouldUseStoreManagerDashboard = normalizedRole === "gerente_geral" && companyStoreCount === 1;
  const forcePasswordChange = Boolean(
    currentUser?.passwordChangeRequired
    || String(currentUser?.passwordChangeReason || "").trim().toLowerCase() === "first_login"
    || String(currentUser?.passwordChangeReason || "").trim().toLowerCase() === "password_expired"
    || Boolean(currentUser?.firstLogin)
    || Number(currentUser?.primeiro_login || 0) === 1
  );
  const logoutAttemptRef = React.useRef(false);

  const onboardingPath = "/company-setup";
  const subscriptionPath = "/subscription-plans";
  const adminLicensePath = "/admin-licencas-assinatura";
  const requiresOnboarding = Boolean(
    currentUser
    && (
      !hasCompanyScope
      || (isGeneralManagerRole && !hasStoreScope)
    )
  );
  const currentPath = String(location?.pathname || "");
  const isChangePasswordPath = currentPath === "/alterar-senha";
  const shouldForceLogoutForMissingStore = Boolean(baseShouldForceLogoutForMissingStore && !isChangePasswordPath);
  const inSubscriptionPage = currentPath === subscriptionPath;
  const inAdminLicensePage = currentPath === adminLicensePath;
  const hasActiveSubscription = activeCompany ? hasEffectiveActiveSubscription(activeCompany, activeStore) : false;

  React.useEffect(() => {
    if (!shouldForceLogoutForMissingStore || logoutAttemptRef.current) return;
    logoutAttemptRef.current = true;
    logout("manual", {
      skipRemote: true,
      notice: "Sem loja atribuida ao seu utilizador. Contacte o seu Gerente para associar uma loja e tente novamente.",
    });
  }, [shouldForceLogoutForMissingStore, logout]);

  React.useEffect(() => {
    logoutAttemptRef.current = false;
  }, [currentUser?.uuid]);

  // ── Guardas síncronas (sem flash de página errada) ──────────────────────
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (isChangePasswordPath) {
    return children;
  }

  if (forcePasswordChange) {
    return <Navigate to="/alterar-senha" replace state={{ reason: currentUser?.passwordChangeReason || "first_login" }} />;
  }

  if (isAdminSuper && currentPath !== "/admin-painel") {
    return <Navigate to="/admin-painel" replace />;
  }

  if (shouldForceLogoutForMissingStore) {
    return <RouteFallback />;
  }

  if (requiresOnboarding && currentPath !== onboardingPath) {
    return <Navigate to={onboardingPath} replace />;
  }

  if (!requiresOnboarding && !activeCompany) {
    if (hasCompanyScope) {
      return <RouteFallback />;
    }

    return children;
  }

  if (!requiresOnboarding && activeCompany && !hasActiveSubscription && !inSubscriptionPage && !inAdminLicensePage) {
    return <Navigate to={subscriptionPath} replace />;
  }

  if (isCashierRole) {
    if (currentPath === "/subscription-plans") {
      return children;
    }

    const isPosAllowed =
      currentPath === "/pos-terminal" ||
      currentPath.startsWith("/pos-terminal/") ||
      currentPath === "/cobrancas" ||
      currentPath.startsWith("/cobrancas/") ||
      currentPath === "/transacoes" ||
      currentPath.startsWith("/transacoes/") ||
      currentPath === "/produtos-vendidos" ||
      currentPath.startsWith("/produtos-vendidos/") ||
      currentPath === "/pos-relatorio-diario" ||
      currentPath.startsWith("/pos-relatorio-diario/");
    if (!isPosAllowed) {
      return <Navigate to="/pos-terminal" replace />;
    }
  }

  if (!isGeneralManagerRoleOnly && currentPath === "/ceo-dashboard") {
    return <Navigate to={resolveRoleHomePath(currentUser?.roleValue, "", currentUser)} replace />;
  }

  if (shouldUseStoreManagerDashboard && currentPath === "/ceo-dashboard") {
    return <Navigate to="/store-manager-dashboard" replace />;
  }

  if (!requiresOnboarding && currentPath === onboardingPath) {
    return <Navigate to={resolveRoleHomePath(currentUser?.roleValue, "", currentUser)} replace />;
  }

  // ── Verificação de manutenção ────────────────────────────────────────────
  const canBypassMaintenance = MAINTENANCE_BYPASS_ROLES.includes(
    String(currentUser?.roleValue || "").toLowerCase()
  );

  if (!blockedPagesReady) {
    return <RouteFallback />;
  }

  if (!blockedPagesTrusted) {
    if (isOffline && isTauriDesktop) {
      return (
        <MaintenancePage
          pageName="Modo Offline Seguro"
          message="Nao existe cache confiavel de bloqueios nesta instalacao. Conecte-se a internet para sincronizar as regras de acesso antes de continuar."
          expectedReturn={null}
        />
      );
    }

    if (!isOffline) {
      return (
        <MaintenancePage
          pageName="Segurança de Navegação"
          message="Nao foi possivel validar as paginas ativas/inativas sem ligacao ao servidor. Conecte-se a internet para continuar com seguranca."
          expectedReturn={null}
        />
      );
    }
  }

  if (blockedPages.length > 0) {
    const currentPath = location?.pathname || "";
    const blocked = blockedPages.find((p) => {
      const rota = String(p?.rota || "").trim();
      // Match exato ou prefixo de rotas dinâmicas (ex: /transacoes/ajustes/:id)
      return currentPath === rota || currentPath.startsWith(rota + "/");
    });

    if (blocked) {
      const isDisabled = Number(blocked?.ativo) === 0;
      const isUnderMaintenance = Number(blocked?.em_manutencao) === 1;

      // Página desativada → invisível para o utilizador, redireciona silenciosamente para o início
      if (isDisabled) {
        const homePath = resolveRoleHomePath(currentUser?.roleValue, "", currentUser);
        return <Navigate to={homePath} replace />;
      }

      // Página em manutenção → mostra tela de manutenção com informação
      // (papéis de bypass continuam autorizados neste cenário)
      if (isUnderMaintenance && !canBypassMaintenance) {
        return (
          <MaintenancePage
            pageName={blocked?.nome || ""}
            message={blocked?.mensagem_manutencao || ""}
            expectedReturn={blocked?.previsao_retorno || null}
          />
        );
      }
    }
  }

  return children;
}

function GuardedNotFoundRoute() {
  const { isAuthenticated, currentUser } = useErpContext();
  const normalizedRole = String(currentUser?.roleValue || "").trim().toLowerCase();
  const hasStoreScope = Boolean(String(currentUser?.storeUuid || "").trim());
  const isGeneralManagerRole = normalizedRole === "gerente_geral";

  if (isAuthenticated && currentUser && !hasOrganizationScope(currentUser) && isGeneralManagerRole) {
    return <Navigate to="/company-setup" replace />;
  }

  if (isAuthenticated && currentUser && !hasStoreScope && !isGeneralManagerRole) {
    return <Navigate to="/login" replace />;
  }

  return <NotFound />;
}

function PublicLoginRoute() {
  const location = useLocation();
  const { isAuthenticated, currentUser, logout } = useErpContext();
  const normalizedRole = String(currentUser?.roleValue || "").trim().toLowerCase();
  const isAdminSuper = isAdminSuperUser(currentUser);
  const hasStoreScope = Boolean(String(currentUser?.storeUuid || "").trim());
  const isGeneralManagerRole = normalizedRole === "gerente_geral";
  const forcePasswordChange = Boolean(
    currentUser?.passwordChangeRequired
    || String(currentUser?.passwordChangeReason || "").trim().toLowerCase() === "first_login"
    || String(currentUser?.passwordChangeReason || "").trim().toLowerCase() === "password_expired"
    || Boolean(currentUser?.firstLogin)
    || Number(currentUser?.primeiro_login || 0) === 1
  );
  // Nao forca logout por falta de loja se o utilizador precisa trocar senha primeiro
  const shouldForceLogoutForMissingStore = Boolean(
    isAuthenticated && currentUser && !hasStoreScope && !isGeneralManagerRole && !isAdminSuper && !forcePasswordChange
  );
  const logoutAttemptedRef = React.useRef(false);

  React.useEffect(() => {
    if (!shouldForceLogoutForMissingStore || logoutAttemptedRef.current) return;
    logoutAttemptedRef.current = true;
    logout("manual", {
      skipRemote: true,
      notice: "Sem loja atribuida ao seu utilizador. Contacte o seu Gerente para associar uma loja e tente novamente.",
    });
  }, [logout, shouldForceLogoutForMissingStore]);

  if (shouldForceLogoutForMissingStore) {
    return <RouteFallback />;
  }

  if (isAuthenticated) {
    if (forcePasswordChange) {
      return <Navigate to="/alterar-senha" replace state={{ reason: currentUser?.passwordChangeReason || "first_login" }} />;
    }
    const nextPath = resolvePostLoginPath(currentUser, [], location?.state?.from?.pathname || "");
    return <Navigate to={nextPath.path} replace state={nextPath.state || undefined} />;
  }

  const cameFromPublicSplash = Boolean(location?.state?.fromSplash);
  if (!cameFromPublicSplash) {
    return <Navigate to="/splash" replace state={{ from: location }} />;
  }

  return <LoginPage />;
}

function PublicSplashRoute() {
  const location = useLocation();
  const { isAuthenticated, currentUser } = useErpContext();
  const forcePasswordChange = Boolean(
    currentUser?.passwordChangeRequired
    || String(currentUser?.passwordChangeReason || "").trim().toLowerCase() === "first_login"
    || String(currentUser?.passwordChangeReason || "").trim().toLowerCase() === "password_expired"
    || Boolean(currentUser?.firstLogin)
    || Number(currentUser?.primeiro_login || 0) === 1
  );

  if (isAuthenticated) {
    if (forcePasswordChange) {
      return <Navigate to="/alterar-senha" replace state={{ reason: currentUser?.passwordChangeReason || "first_login" }} />;
    }

    const nextPath = resolvePostLoginPath(currentUser, [], location?.state?.from?.pathname || "");
    return <Navigate to={nextPath.path} replace state={nextPath.state || undefined} />;
  }

  return <PublicSplashPage />;
}

function isAdminSuperUser(user) {
  const roleValue = String(user?.roleValue || "").trim().toLowerCase();
  const rawRoleCode = String(user?.rawRoleCode || user?.papel_codigo || "").trim().toLowerCase();
  const rawRoleName = String(user?.rawRoleName || user?.title || user?.papel_nome || user?.roleName || "").trim().toLowerCase();

  return roleValue === "admin_super"
    || rawRoleCode === "admin_super"
    || rawRoleCode === "admin super"
    || rawRoleName === "administrador super"
    || rawRoleName === "admin super";
}

function AdminSuperOnlyRoute({ children }) {
  const location = useLocation();
  const { isAuthenticated, currentUser } = useErpContext();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (!isAdminSuperUser(currentUser)) {
    const nextPath = resolvePostLoginPath(currentUser, [], "");
    return <Navigate to={nextPath.path} replace state={nextPath.state || undefined} />;
  }

  return children;
}

function HomeRoute() {
  const { currentUser } = useErpContext();
  return <Navigate to={resolveRoleHomePath(currentUser?.roleValue, "", currentUser)} replace />;
}

function LegacyRetificarRoute() {
  const location = useLocation();
  const match = location?.pathname?.match(/\/transacoes\/retificar\/(.+)$/i);
  const uuidVenda = match?.[1] || "";
  return <Navigate to={`/transacoes/ajustes/${uuidVenda}?acao=retificar`} replace />;
}

function LegacyCancelarRoute() {
  const location = useLocation();
  const match = location?.pathname?.match(/\/transacoes\/cancelar\/(.+)$/i);
  const uuidVenda = match?.[1] || "";
  return <Navigate to={`/transacoes/ajustes/${uuidVenda}?acao=cancelar`} replace />;
}

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ScrollToTop />
      <Suspense fallback={<RouteFallback />}>
        <RouteSeoGuard />
        <AuthSessionGuard />
        <StartupSplashGate />
        <RouterRoutes>
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/splash" element={<PublicSplashRoute />} />
          <Route path="/login" element={<PublicLoginRoute />} />
          <Route path="/alterar-senha" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />
          <Route path="/register" element={<AdminSuperOnlyRoute><RegisterPage /></AdminSuperOnlyRoute>} />
          <Route path="/company-setup" element={<ProtectedRoute><CompanySetupPage /></ProtectedRoute>} />
          <Route path="/subscription-plans" element={<ProtectedRoute><SubscriptionPlansPage /></ProtectedRoute>} />
          <Route path="/admin-painel" element={<AdminSuperOnlyRoute><AdminPanelPage /></AdminSuperOnlyRoute>} />
          <Route path="/admin-licencas-assinatura" element={<ProtectedRoute><AdminSignatureLicensesPage /></ProtectedRoute>} />
          <Route path="/company-registration" element={<Navigate to="/company-setup" replace />} />
          <Route path="/" element={<ProtectedRoute><HomeRoute /></ProtectedRoute>} />
          <Route path="/manager-overview" element={<ProtectedRoute><ManagerOverview /></ProtectedRoute>} />
          <Route path="/ceo-dashboard" element={<ProtectedRoute><CeoDashboard /></ProtectedRoute>} />
          <Route path="/store-manager-dashboard" element={<ProtectedRoute><StoreManagerDashboard /></ProtectedRoute>} />
          <Route path="/executive-kpi-dashboard" element={<ProtectedRoute><ExecutiveKpiDashboard /></ProtectedRoute>} />
          <Route path="/financial-compliance-hub" element={<ProtectedRoute><FinancialComplianceHub /></ProtectedRoute>} />
          <Route path="/agt-management" element={<ProtectedRoute><AgtManagement /></ProtectedRoute>} />
          <Route path="/sales-analytics-dashboard" element={<ProtectedRoute><SalesAnalyticsDashboard /></ProtectedRoute>} />
          <Route path="/pos-terminal" element={<ProtectedRoute><PosTerminal /></ProtectedRoute>} />
          <Route path="/pos-terminal/sucesso" element={<ProtectedRoute><PosSaleSuccess /></ProtectedRoute>} />
          <Route path="/customer-registry" element={<ProtectedRoute><CustomerRegistry /></ProtectedRoute>} />
          <Route path="/product-catalog" element={<ProtectedRoute><ProductCatalog /></ProtectedRoute>} />
          <Route path="/transacoes" element={<ProtectedRoute><Transacoes /></ProtectedRoute>} />
          <Route path="/transacoes/ajustes/:uuidVenda" element={<ProtectedRoute><TransacoesAjustes /></ProtectedRoute>} />
          <Route path="/transacoes/validar/:uuidVenda" element={<ProtectedRoute><TransacoesValidar /></ProtectedRoute>} />
          <Route path="/transacoes/documentos/:uuidVenda" element={<ProtectedRoute><TransacoesDocumentos /></ProtectedRoute>} />
          <Route path="/transacoes/documentos-lista/:uuidVenda" element={<ProtectedRoute><TransacoesDocumentosLista /></ProtectedRoute>} />
          <Route path="/produtos-vendidos" element={<ProtectedRoute><ProdutosVendidosPage /></ProtectedRoute>} />
          <Route path="/observability-events" element={<AdminSuperOnlyRoute><ObservabilityEventsPage /></AdminSuperOnlyRoute>} />
          <Route path="/assistant-telemetry" element={<AdminSuperOnlyRoute><AssistantTelemetryPage /></AdminSuperOnlyRoute>} />
          <Route path="/cobrancas" element={<ProtectedRoute><CobrancasPage /></ProtectedRoute>} />
          <Route path="/vendor-management" element={<ProtectedRoute><VendorManagementPage /></ProtectedRoute>} />
          <Route path="/pos-relatorio-diario" element={<ProtectedRoute><PosDailyReportPage /></ProtectedRoute>} />
          <Route path="/access-control" element={<ProtectedRoute><AccessControlPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/home-splash" element={<ProtectedRoute><SplashHomePage /></ProtectedRoute>} />
          <Route path="/profile-settings" element={<ProtectedRoute><ProfileSettingsPage /></ProtectedRoute>} />
          <Route path="/transacoes/retificar/:uuidVenda" element={<ProtectedRoute><LegacyRetificarRoute /></ProtectedRoute>} />
          <Route path="/transacoes/cancelar/:uuidVenda" element={<ProtectedRoute><LegacyCancelarRoute /></ProtectedRoute>} />
          <Route path="*" element={<GuardedNotFoundRoute />} />
        </RouterRoutes>
      </Suspense>
      <FloatingChatbot />
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
